import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * One client, configured once.
 *
 * The defaults matter more than usual here because this panel watches live
 * hardware: a fleet view that shows a five-minute-old connection state is
 * actively misleading, and a view that refetches every second is a load
 * generator pointed at the operational database.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * Connection state comes from an in-memory registry and changes without
         * any action from this client, so cached fleet data is stale almost
         * immediately. Individual views set their own `refetchInterval`; the
         * short default just stops a back-navigation from showing yesterday.
         */
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        /**
         * Never retry a request the server answered deliberately. A 401, 403, 404
         * or 429 means the same thing three times, and retrying a 429 is how a
         * rate limit becomes a lockout.
         */
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        // Commands are not idempotent — spec 4.3 is explicit that a duplicated
        // RemoteStartTransaction starts two sessions. Nothing here retries.
        retry: false,
      },
    },
  });
}
